import type { LocalVault } from '@signbento/storage'

/**
 * Sign in once per device, with local records owned by their account (sign-in-once spec §1,
 * Hamza's decision 6, 13 Oct 2026).
 *
 * With the gate on, every account on a device gets **its own vault**: its own IndexedDB database,
 * blob directory and data key (`openWebVault` keys everything by vault id). So:
 *
 * - a different account starts with an empty workspace, and the app never lists or offers another
 *   account's records, because they live in a vault it never opens for that account;
 * - signing out closes the vault and wipes nothing; the owner's vault reopens on their next
 *   sign-in, unchanged;
 * - the vault a device used **before** the gate (the guest vault) is unowned. It is offered once,
 *   to the first account that signs in on the device: attaching makes it that account's vault (no
 *   copy, nothing moves), declining leaves it unowned and hidden. Only that first account is ever
 *   asked, even if it closes the app without answering.
 *
 * This is isolation **through the app**: the ids live in `localStorage` and every vault's key is
 * in the same browser profile, so it does not hold against another local user with devtools.
 *
 * Every change to device ownership (bind, the guest answer, release, and a map rebuild) runs
 * under one exclusive lock shared by all tabs (`withDeviceAccountLock`, the Web Locks API), and
 * reads what it decides on **inside** that lock. Each bind or guest answer also takes a fresh
 * operation token when it is requested, and commits only if its token is still the device's
 * latest: a sign-in that a later sign-in or sign-out overtook never writes, even if it was
 * already waiting on a read. So the most recent request on the device is the one that ends up
 * current, and the guest vault is offered and attached once at most, whatever the tabs
 * interleave. A sign-out always commits (it only ever removes the current account).
 *
 * Which account is current, and which vault each account owns, are device facts kept in
 * `localStorage` (ids only, no secrets). The vault contents stay encrypted per vault.
 */

/** The guest vault's id: the key the app used before the gate (`vault-context.tsx`). */
export const GUEST_VAULT_ID_KEY = 'signbento.vault_id'
export const CURRENT_ACCOUNT_KEY = 'signbento.account.current'
export const ACCOUNT_VAULTS_KEY = 'signbento.account.vaults'
/** A damaged map is copied here before anything rewrites it. */
export const ACCOUNT_VAULTS_BACKUP_KEY = 'signbento.account.vaults.backup'
/** `attached:<account id>` or `declined`: the guest vault's one-time decision. */
export const GUEST_VAULT_DECISION_KEY = 'signbento.guest_vault.decision'
/** The one account the guest vault was offered to; no other account is ever asked. */
export const GUEST_VAULT_OFFERED_KEY = 'signbento.guest_vault.offered_to'
/** The token of the latest bind, guest answer or release requested on this device. */
export const ACCOUNT_OPERATION_KEY = 'signbento.account.operation'

/** The setting each vault stores its owner in (written at every sign-in, `SignIn.tsx`). */
const VAULT_OWNER_SETTING = 'account.current_id'

export type DeviceStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

export type BindOutcome =
  | { readonly kind: 'BOUND'; readonly vault: LocalVault; readonly vault_id: string }
  /** The device has unowned guest records: the person decides once whether they are theirs. */
  | {
      readonly kind: 'GUEST_CHOICE'
      readonly document_count: number
      readonly template_count: number
      readonly other_count: number
    }
  /** A later sign-in or sign-out on this device overtook this one; it changed nothing. */
  | { readonly kind: 'SUPERSEDED' }

export interface DeviceAccountVaults {
  /** The account this device is signed in to, or null (the gate shows sign-in). */
  current(): string | null
  /** Opens the current account's vault, or null when no account is current. */
  openCurrent(): Promise<{ readonly vault: LocalVault; readonly vault_id: string } | null>
  /** After a verified sign-in: the account's vault, or the one-time guest-records question. */
  bind(accountId: string): Promise<BindOutcome>
  /** The answer to the guest-records question; final for this device. */
  resolveGuestChoice(
    accountId: string,
    attach: boolean,
  ): Promise<Exclude<BindOutcome, { kind: 'GUEST_CHOICE' }>>
  /** Sign out on this device: no account is current; nothing is wiped. */
  release(): Promise<void>
}

/** Runs `work` while holding the device-ownership lock; every tab must use the same one. */
export type DeviceLock = <T>(work: () => Promise<T>) => Promise<T>

/** A lock for one tab only: the fallback where the Web Locks API is missing. */
export function createInTabLock(): DeviceLock {
  let tail: Promise<unknown> = Promise.resolve()
  return <T>(work: () => Promise<T>): Promise<T> => {
    const run = tail.then(work, work)
    tail = run.catch(() => undefined)
    return run
  }
}

const inTabLock = createInTabLock()

/**
 * The lock every tab shares: one exclusive Web Lock named for device ownership, granted in
 * request order. Where the API is missing it falls back to a lock for this tab alone.
 */
export const withDeviceAccountLock: DeviceLock = <T>(work: () => Promise<T>): Promise<T> => {
  const locks = (globalThis.navigator as { locks?: LockManager } | undefined)?.locks
  if (locks === undefined) return inTabLock(work)
  return locks.request('signbento.accounts', { mode: 'exclusive' }, work) as Promise<T>
}

type VaultMapRead =
  | { readonly kind: 'OK'; readonly map: Readonly<Record<string, string>> }
  | { readonly kind: 'DAMAGED'; readonly raw: string }

export function createDeviceAccountVaults(options: {
  readonly storage: DeviceStorage
  readonly open: (vaultId: string) => Promise<LocalVault>
  readonly newVaultId: () => string
  /** Every vault id that exists on this device, for rebuilding a damaged map. */
  readonly knownVaultIds?: () => Promise<readonly string[]>
  /** The device-ownership lock shared by every tab (`withDeviceAccountLock` in the app). */
  readonly lock?: DeviceLock
  /** A fresh operation token (a random UUID in the app). */
  readonly newToken?: () => string
}): DeviceAccountVaults {
  const { storage } = options
  const lock = options.lock ?? withDeviceAccountLock
  const newToken = options.newToken ?? (() => crypto.randomUUID())

  /** Marks a new request as the device's latest; every earlier bind or answer is now stale. */
  function request(): string {
    const token = newToken()
    storage.setItem(ACCOUNT_OPERATION_KEY, token)
    return token
  }

  /** Checked right before each write, with no await in between. */
  function latest(token: string): boolean {
    return storage.getItem(ACCOUNT_OPERATION_KEY) === token
  }

  const SUPERSEDED = { kind: 'SUPERSEDED' } as const

  function readMap(): VaultMapRead {
    const raw = storage.getItem(ACCOUNT_VAULTS_KEY)
    if (raw === null) return { kind: 'OK', map: {} }
    try {
      const parsed: unknown = JSON.parse(raw)
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { kind: 'DAMAGED', raw }
      }
      const map: Record<string, string> = {}
      for (const [account, vault] of Object.entries(parsed)) {
        if (typeof vault === 'string' && vault !== '') map[account] = vault
      }
      return { kind: 'OK', map }
    } catch {
      return { kind: 'DAMAGED', raw }
    }
  }

  function guestVaultId(): string | null {
    const id = storage.getItem(GUEST_VAULT_ID_KEY)
    return id === null || id === '' ? null : id
  }

  /**
   * The map, repaired when it is damaged: the raw value is kept in a backup key first, then the
   * map is rebuilt from the owner each vault records. The guest vault is never mapped this way
   * unless its one-time decision attached it to that account.
   */
  async function vaultMap(): Promise<Readonly<Record<string, string>>> {
    const read = readMap()
    if (read.kind === 'OK') return read.map
    if (storage.getItem(ACCOUNT_VAULTS_BACKUP_KEY) === null) {
      storage.setItem(ACCOUNT_VAULTS_BACKUP_KEY, read.raw)
    }
    const rebuilt: Record<string, string> = {}
    const guestId = guestVaultId()
    const decision = storage.getItem(GUEST_VAULT_DECISION_KEY)
    for (const vaultId of (await options.knownVaultIds?.()) ?? []) {
      let owner: string | null = null
      try {
        owner = await (await options.open(vaultId)).getSetting<string>(VAULT_OWNER_SETTING)
      } catch {
        continue
      }
      if (typeof owner !== 'string' || owner === '' || rebuilt[owner] !== undefined) continue
      if (vaultId === guestId && decision !== `attached:${owner}`) continue
      rebuilt[owner] = vaultId
    }
    storage.setItem(ACCOUNT_VAULTS_KEY, JSON.stringify(rebuilt))
    return rebuilt
  }

  /**
   * Makes the account current with this vault, unless a later request overtook this one. A guest
   * decision commits in the same synchronous step, so an overtaken claim leaves nothing behind.
   */
  async function bound(token: string, accountId: string, vaultId: string, decision?: string) {
    const map = await vaultMap()
    if (!latest(token)) return SUPERSEDED
    if (decision !== undefined) storage.setItem(GUEST_VAULT_DECISION_KEY, decision)
    storage.setItem(ACCOUNT_VAULTS_KEY, JSON.stringify({ ...map, [accountId]: vaultId }))
    storage.setItem(CURRENT_ACCOUNT_KEY, accountId)
    return { kind: 'BOUND' as const, vault: await options.open(vaultId), vault_id: vaultId }
  }

  /** Every kind of local record the spec counts (§1): any one of them makes the vault claimable. */
  async function guestRecordCounts(guest: LocalVault) {
    const [documents, drafts, templates, workflows, signatures, completions, myInfo] =
      await Promise.all([
        guest.listDocuments(),
        guest.listDrafts(),
        guest.listTemplates(),
        guest.listWorkflows(),
        guest.listSignatures(),
        guest.listCompletions(),
        guest.getMyInfo(),
      ])
    return {
      documents: documents.length,
      templates: templates.length,
      other:
        drafts.length +
        workflows.length +
        signatures.length +
        completions.length +
        (myInfo === null ? 0 : 1),
    }
  }

  /**
   * The guest vault, when this account may be asked about it and it holds something to claim.
   * The offer is re-checked after the reads, right before it is written.
   */
  async function pendingGuestRecords(token: string, accountId: string) {
    const mayAsk = () =>
      storage.getItem(GUEST_VAULT_DECISION_KEY) === null &&
      [null, accountId].includes(storage.getItem(GUEST_VAULT_OFFERED_KEY))
    if (!mayAsk()) return null
    const guestId = guestVaultId()
    if (guestId === null || Object.values(await vaultMap()).includes(guestId)) return null
    const counts = await guestRecordCounts(await options.open(guestId))
    if (!latest(token)) return SUPERSEDED
    if (!mayAsk()) return null
    if (counts.documents + counts.templates + counts.other === 0) {
      // Nothing of any kind to claim: record that, so the question never appears for it.
      storage.setItem(GUEST_VAULT_DECISION_KEY, 'declined')
      return null
    }
    // From now on only this account is ever asked, even if it leaves without answering.
    storage.setItem(GUEST_VAULT_OFFERED_KEY, accountId)
    return counts
  }

  return {
    current() {
      const id = storage.getItem(CURRENT_ACCOUNT_KEY)
      return id === null || id === '' ? null : id
    },

    openCurrent() {
      return lock(async () => {
        const accountId = this.current()
        if (accountId === null) return null
        const vaultId = (await vaultMap())[accountId]
        if (vaultId === undefined) return null
        return { vault: await options.open(vaultId), vault_id: vaultId }
      })
    },

    bind(accountId) {
      const token = request()
      return lock(async () => {
        const owned = (await vaultMap())[accountId]
        if (owned !== undefined) return bound(token, accountId, owned)
        const pending = await pendingGuestRecords(token, accountId)
        if (pending !== null && 'kind' in pending) return pending
        if (pending !== null) {
          return {
            kind: 'GUEST_CHOICE' as const,
            document_count: pending.documents,
            template_count: pending.templates,
            other_count: pending.other,
          }
        }
        return bound(token, accountId, options.newVaultId())
      })
    },

    resolveGuestChoice(accountId, attach) {
      const token = request()
      return lock(async () => {
        const map = await vaultMap()
        // Everything this decides on is read here, after the last await and any earlier claim.
        if (!latest(token)) return SUPERSEDED
        // Already answered for this account (another tab, or a repeat): keep the vault it owns.
        const owned = map[accountId]
        if (owned !== undefined) return bound(token, accountId, owned)
        const guestId = guestVaultId()
        const askedThisAccount = storage.getItem(GUEST_VAULT_OFFERED_KEY) === accountId
        const undecided = storage.getItem(GUEST_VAULT_DECISION_KEY) === null
        const claimable =
          attach &&
          guestId !== null &&
          undecided &&
          askedThisAccount &&
          !Object.values(map).includes(guestId)
        // Only the account that was asked records the decision, and only once, with the binding.
        const decision =
          askedThisAccount && undecided
            ? claimable
              ? `attached:${accountId}`
              : 'declined'
            : undefined
        return bound(
          token,
          accountId,
          claimable && guestId !== null ? guestId : options.newVaultId(),
          decision,
        )
      })
    },

    release() {
      // A sign-out overtakes every earlier request and always commits: it only removes.
      request()
      return lock(async () => {
        storage.removeItem(CURRENT_ACCOUNT_KEY)
      })
    },
  }
}

/**
 * Calls back when another tab of this app changes which account is current (or clears storage),
 * so every open tab drops or reopens its vault instead of keeping the previous account's.
 * Returns the unsubscribe function.
 */
export function onCurrentAccountChange(
  target: Pick<Window, 'addEventListener' | 'removeEventListener'>,
  callback: (accountId: string | null) => void,
): () => void {
  const listener = (event: Event) => {
    const storageEvent = event as StorageEvent
    if (storageEvent.key !== null && storageEvent.key !== CURRENT_ACCOUNT_KEY) return
    const next = storageEvent.key === null ? null : storageEvent.newValue
    callback(next === null || next === '' ? null : next)
  }
  target.addEventListener('storage', listener)
  return () => target.removeEventListener('storage', listener)
}

/** The gate is a build flag: on in dev, off in production until W4, W5 and native sign-in. */
export function signInRequired(): boolean {
  return import.meta.env.VITE_SIGN_IN_REQUIRED === 'on'
}
